import { useParams, useNavigate } from "react-router-dom";
import { format, parse, isValid } from "date-fns";
import { ChevronLeft } from "lucide-react";
import { useGetQuotationById } from "../service/quatationService";
import { useGetASalesInvoice } from "../service/invoiseService";
import Loading from "../components/common/LoaderCommon";

type SalesDetailProps = {
  type: "draft" | "quotation" | "invoice";
};

const formatSalesDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return "-";
  const standardDate = new Date(dateStr);
  if (isValid(standardDate)) {
    return format(standardDate, "dd-MM-yyyy hh:mm a");
  }
  const parsedDate = parse(dateStr, "dd-MM-yyyy", new Date());
  if (isValid(parsedDate)) {
    return format(parsedDate, "dd-MM-yyyy");
  }
  return dateStr;
};

export default function SalesDetail({ type }: SalesDetailProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isQuotationOrDraft = type === "draft" || type === "quotation";
  const { data: quotationData, isLoading: quoteLoading } = useGetQuotationById(
    id || "",
  );
  const { data: invoiceData, isLoading: invoiceLoading } = useGetASalesInvoice(
    isQuotationOrDraft ? undefined : id,
  );

  if (quoteLoading || invoiceLoading) {
    return <Loading />;
  }

  const data = isQuotationOrDraft ? quotationData : invoiceData;

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-xl font-semibold text-gray-700">
          Record not found
        </h2>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-[#1A5EF9] text-white rounded-[12px] hover:bg-[#0E4FDE] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  // Normalize data between Quotation and Invoice
  const customer = data.customer;
  const documentNumber = isQuotationOrDraft
    ? (data as any).quotationNumber
    : (data as any).invoiceNumber;
  const documentDate = isQuotationOrDraft
    ? (data as any).quotationDate
    : (data as any).salesDate;
  const products = isQuotationOrDraft
    ? (data as any).quotationProducts || []
    : (data as any).invoiceProductResponses || [];
  const status =
    (data as any).status || (data as any).paymentStatus || "PENDING";

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/50 transition-colors bg-white shadow-sm"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1A5EF9] capitalize flex items-center gap-3">
            {type} Detail
            <span className="px-3 py-1 text-xs rounded-full bg-[#E8EEF9] text-[#1A5EF9] font-semibold border border-[#D7E2F5]">
              {status}
            </span>
          </h1>
          <p className="text-sm text-[#7D8998] mt-1">
            {documentNumber} • {formatSalesDate(documentDate)}
          </p>
        </div>
      </div>

      {/* Customer Info Card */}
      <div className="bg-white rounded-[24px] p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)] border border-[#E8EEF9]">
        <h2 className="text-lg font-semibold text-[#21314A] mb-5">
          Customer Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <p className="text-xs text-[#8A9BB5] uppercase tracking-wider mb-1 font-medium">
              Name
            </p>
            <p className="font-medium text-[#21314A]">
              {customer?.username || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-[#8A9BB5] uppercase tracking-wider mb-1 font-medium">
              Phone
            </p>
            <p className="font-medium text-[#21314A]">
              {customer?.phoneNumber || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-[#8A9BB5] uppercase tracking-wider mb-1 font-medium">
              Email
            </p>
            <p className="font-medium text-[#21314A]">
              {customer?.email || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs text-[#8A9BB5] uppercase tracking-wider mb-1 font-medium">
              TRN
            </p>
            <p className="font-medium text-[#21314A]">
              {(data as any).trnnumber || "-"}
            </p>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-[24px] shadow-[0_16px_40px_rgba(26,94,249,0.08)] border border-[#E8EEF9] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#E8EEF9]">
          <h2 className="text-lg font-semibold text-[#21314A]">Products</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-[#52627C]">
            <thead className="bg-[#F5F8FD] text-[#62738E] text-xs font-semibold">
              <tr>
                <th className="px-6 py-4 whitespace-nowrap">Product Name</th>
                <th className="px-6 py-4 whitespace-nowrap text-right">Qty</th>
                <th className="px-6 py-4 whitespace-nowrap text-right">
                  Unit Price
                </th>
                <th className="px-6 py-4 whitespace-nowrap text-right">
                  Discount
                </th>
                <th className="px-6 py-4 whitespace-nowrap text-right">
                  Tax (%)
                </th>
                <th className="px-6 py-4 whitespace-nowrap text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4ECFA]">
              {products.map((item: any, index: number) => {
                const productName =
                  item.product?.productName || item.product?.name || "-";
                return (
                  <tr
                    key={item.id || index}
                    className="hover:bg-[#F9FAFC] transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-[#21314A]">
                      {productName}
                    </td>
                    <td className="px-6 py-4 text-right">{item.qty}</td>
                    <td className="px-6 py-4 text-right">
                      {Number(item.unitPrice || 0).toFixed(3)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {Number(item.discountAmount || 0).toFixed(3)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {item.rateOfTax || 0}%
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-[#1A5EF9]">
                      {Number(item.totalAmount || 0).toFixed(3)}
                    </td>
                  </tr>
                );
              })}
              {products.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-[#8A9BB5]"
                  >
                    No products found in this document.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="flex justify-end">
        <div className="bg-[#F5F8FD] rounded-[20px] p-6 shadow-sm border border-[#E8EEF9] w-full md:w-96 space-y-4">
          <div className="flex justify-between text-[#52627C]">
            <span className="font-medium">Discount Amount</span>
            <span className="font-semibold text-[#21314A]">
              {Number(data.discountAmount || 0).toFixed(3)}
            </span>
          </div>
          {(data as any).roundingAmount !== undefined && (
            <div className="flex justify-between text-[#52627C]">
              <span className="font-medium">Rounding</span>
              <span className="font-semibold text-[#21314A]">
                {Number((data as any).roundingAmount || 0).toFixed(3)}
              </span>
            </div>
          )}
          <div className="pt-4 border-t border-[#D7E2F5] flex justify-between items-center">
            <span className="font-bold text-[#21314A] text-lg">
              Grand Total
            </span>
            <span className="font-bold text-[#1A5EF9] text-2xl">
              {Number(data.totalAmount || 0).toFixed(3)}
            </span>
          </div>
          {(data as any).paidAmount !== undefined && (
            <div className="flex justify-between text-[#52627C]">
              <span className="font-medium">Paid Amount</span>
              <span className="font-semibold text-[#22C55E]">
                {Number((data as any).paidAmount || 0).toFixed(3)}
              </span>
            </div>
          )}
          <div className="flex justify-between text-[#52627C]">
            <span className="font-medium">Returned Amount</span>
            <span className="font-semibold text-[#F59E0B]">
              {(data as any).returnedAmount !== undefined && (data as any).returnedAmount !== null
                ? Number((data as any).returnedAmount).toFixed(3)
                : "----"}
            </span>
          </div>
          <div className="flex justify-between text-[#52627C]">
            <span className="font-medium">Written-Off Amount</span>
            <span className="font-semibold text-[#8B5CF6]">
              {(data as any).writtenOffAmount !== undefined && (data as any).writtenOffAmount !== null
                ? Number((data as any).writtenOffAmount).toFixed(3)
                : "----"}
            </span>
          </div>
          {(data as any).balanceAmount !== undefined && (
            <div className="flex justify-between text-[#52627C]">
              <span className="font-medium">Balance Amount</span>
              <span className="font-semibold text-[#EF4444]">
                {Number((data as any).balanceAmount || 0).toFixed(3)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
